import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "../App";
import { useLanguage, useTranslation } from "../contexts/LanguageContext";
import BottomNav from "../components/BottomNav";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  ArrowLeft, Moon, Sun, Bell, Shield, Trash2, 
  ChevronRight, AlertTriangle
} from "lucide-react";

import { API } from "../config";
import { WebSubscriptionCard } from "../components/checkout/WebSubscriptionCard";

export default function Settings() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notifications, setNotifications] = useState(true);

  const handleThemeChange = async (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/users/preferences`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ theme: newTheme })
      });
    } catch (error) {
      console.error("Error saving preference:", error);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/users/account`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` },
      });

      if (response.ok) {
        localStorage.removeItem("user");
        localStorage.removeItem("session_token");
        alert(t('settings.deleteSuccess'));
        navigate("/");
      } else {
        const data = await response.json();
        alert(data.detail || t('settings.deleteError'));
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert(t('settings.deleteError'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="settings-page">
      
      <div className="pt-6 pb-24 px-4">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/profile")}
              className="text-white"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <h1 className="font-heading text-2xl font-bold text-white">
              {t('settings.title')}
            </h1>
          </div>

          {/* Theme Section */}
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              {t('settings.appearance')}
            </h2>
            
            <div className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden">
              <button
                onClick={() => handleThemeChange("dark")}
                className={`w-full p-4 flex items-center gap-4 transition-all ${
                  theme === "dark" ? "bg-primary/20" : "hover:bg-white/5"
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  theme === "dark" ? "bg-primary" : "bg-muted/50"
                }`}>
                  <Moon className={`w-5 h-5 ${theme === "dark" ? "text-white" : "text-muted-foreground"}`} />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">{t('settings.darkMode')}</p>
                  <p className="text-sm text-muted-foreground">{t('settings.darkModeDesc')}</p>
                </div>
                {theme === "dark" && (
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>
                )}
              </button>
              
              <div className="border-t border-white/5" />
              
              <button
                onClick={() => handleThemeChange("light")}
                className={`w-full p-4 flex items-center gap-4 transition-all ${
                  theme === "light" ? "bg-primary/20" : "hover:bg-white/5"
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  theme === "light" ? "bg-primary" : "bg-muted/50"
                }`}>
                  <Sun className={`w-5 h-5 ${theme === "light" ? "text-white" : "text-muted-foreground"}`} />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">{t('settings.lightMode')}</p>
                  <p className="text-sm text-muted-foreground">{t('settings.lightModeDesc')}</p>
                </div>
                {theme === "light" && (
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Notifications Section */}
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              {t('settings.notifications')}
            </h2>
            
            <div className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-blue-500" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{t('settings.pushNotifications')}</p>
                  <p className="text-sm text-muted-foreground">{t('settings.receiveAlerts')}</p>
                </div>
                <Switch 
                  checked={notifications} 
                  onCheckedChange={setNotifications}
                />
              </div>
            </div>
          </div>

          <WebSubscriptionCard user={user} setUser={setUser} en={language === "en"} />

          {/* Privacy Section */}
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              {t('settings.privacy')}
            </h2>
            
            <div className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden">
              <button
                onClick={() => navigate("/privacy-policy")}
                className="w-full p-4 flex items-center gap-4 hover:bg-white/5 transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-500" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">{t('settings.privacyPolicy')}</p>
                  <p className="text-sm text-muted-foreground">{t('settings.dataProtected')}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Danger Zone */}
          <div>
            <h2 className="text-sm font-semibold text-red-500 uppercase tracking-wider mb-4">
              {t('settings.dangerZone')}
            </h2>
            
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl overflow-hidden">
              <button
                onClick={() => setDeleteDialogOpen(true)}
                className="w-full p-4 flex items-center gap-4 hover:bg-red-500/20 transition-all"
                data-testid="delete-account-btn"
              >
                <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-500" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-red-500 font-medium">{t('settings.deleteAccount')}</p>
                  <p className="text-sm text-red-500/70">{t('settings.deleteAccountDesc')}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-red-500" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <DialogTitle className="text-center text-white">
              {t('settings.deleteConfirmTitle')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('settings.deleteConfirmDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="flex-1 rounded-full"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full"
              data-testid="confirm-delete-btn"
            >
              {deleting ? t('common.loading') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
